"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DateSelect = DateSelect;
var _theme = require("@a1s/theme");
var _chronoNode = require("chrono-node");
var _dateFns = require("date-fns");
var _nanoid = require("nanoid");
var _polished = require("polished");
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _CalendarPicker = require("../CalendarPicker");
var _state = require("./state");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
//
// Styled components
// -------------------------------------------------------------------------------------------------
/**
 * The expanded section of the form that contains the calendar picker.
 */
const Calendar = _styledComponents.default.div`
  ${p => (0, _theme.adjustFontSizeTo)(p.theme.fontSizes[1])};

  align-items: stretch;
  background-color: ${p => p.theme.colors.white};
  backface-visibility: hidden;
  border: ${p => p.theme.colors.deepSkyBlue} solid ${(0, _polished.rem)(2)};
  border-radius: 0 0 ${p => (0, _polished.rem)(p.theme.radii[1])} ${p => (0, _polished.rem)(p.theme.radii[1])};
  border-top: 0;
  bottom: calc(0 - 100%);
  box-shadow: ${({
  theme
}) => (0, _polished.transparentize)(0.92, theme.colors.raisinBlack)} 0 ${(0, _polished.rem)(4)} ${(0, _polished.rem)(8)};
  display: flex;
  flex-direction: column;
  list-style: none;
  overflow-y: auto;
  position: absolute;

  width: 100%;
  z-index: 2;

  [aria-expanded='false'] > & {
    display: none;
  }

  & > * {
    border-top: ${p => p.theme.colors.antiFlashWhite} solid ${(0, _polished.rem)(1)};
  }
`;

/**
 * The top-level wrapper componenent that contains the rest of the components.
 */
const Container = _styledComponents.default.div`
  cursor: text;
  position: relative;
  user-select: none;
  width: 100%;
`;

/**
 * Contains the main part of the component that is always visible, whether the component is expanded or collapsed. It
 * resembles all of the other form elements, including the `TextInput` component.
 */
const Control = _styledComponents.default.div`
  align-items: start;
  background-color: ${p => p.theme.colors.antiFlashWhite};
  border: ${p => p.theme.colors.antiFlashWhite} solid ${(0, _polished.rem)(2)};
  border-radius: ${p => (0, _polished.rem)(p.theme.radii[1])};
  display: flex;
  justify-content: stretch;
  padding: ${p => (0, _polished.rem)(p.theme.spacing[0])} ${p => (0, _polished.rem)(p.theme.spacing[1])};
  position: relative;
  white-space: nowrap;
  z-index: 1;

  [aria-expanded='true'] > & {
    background-color: ${p => p.theme.colors.white};
    border-bottom: none;
    border-color: ${p => p.theme.colors.deepSkyBlue};
    border-radius: ${p => (0, _polished.rem)(p.theme.radii[1])} ${p => (0, _polished.rem)(p.theme.radii[1])} 0 0;
    box-shadow: ${({
  theme
}) => (0, _polished.transparentize)(0.92, theme.colors.raisinBlack)} 0 ${(0, _polished.rem)(4)} ${(0, _polished.rem)(8)};
  }
`;

/**
 * The input element for typing in the date directly.
 */
const Input = _styledComponents.default.input`
  ${p => (0, _theme.adjustFontSizeTo)(p.theme.fontSizes[1])};

  appearance: none;
  background-color: transparent;
  border: none;
  display: inline-block;
  flex-grow: 1;
  font-family: ${p => p.theme.ui.baseFont};
  margin: 0;
  outline: 0;
  padding: 0;
`;

//
// Main component
// -------------------------------------------------------------------------------------------------
function DateSelect({
  disabled,
  maxDate,
  minDate,
  name,
  placeholder,
  testID,
  value,
  ...events
}) {
  const {
    onBlur,
    onChange,
    onFocus
  } = events;
  const containerRef = (0, _react.useRef)(null);
  const idRef = (0, _react.useRef)((0, _nanoid.nanoid)());
  const inputRef = (0, _react.useRef)(null);
  const [state, dispatch] = (0, _react.useReducer)(_state.reducer, null, _state.initializer);
  const [displayed, setDisplayed] = (0, _react.useState)(value && (0, _dateFns.format)(value, 'MMMM do yyyy'));

  /**format(value, 'MMMM do yyyy')
   * Blur the entire field if there is a click outside of the component.
   */
  (0, _react.useEffect)(() => {
    function handleWindowClick(event) {
      const target = event.target;
      const current = containerRef.current;
      if (current && !(current.isSameNode(target) || current.contains(target))) {
        var _inputRef$current;
        dispatch({
          type: 'collapse-picker'
        });
        if (onBlur) onBlur(name);
        (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.blur();
      }
    }
    if (state.isExpanded) window.addEventListener('click', handleWindowClick);
    return () => {
      if (state.isExpanded) window.removeEventListener('click', handleWindowClick);
    };
  }, [dispatch, name, onBlur, state]);

  // Change the displayed value the `value` prop changes.
  (0, _react.useEffect)(() => {
    setDisplayed(value ? (0, _dateFns.format)(value, 'MMMM do yyyy') : value);
  }, [value]);
  function handleChange(event) {
    setDisplayed(event.target.value);
  }

  /**
   * Callback for when a date on the calendar is pressed.
   */
  function handleDatePress(date) {
    dispatch({
      type: 'collapse-picker'
    });
    if (onBlur) onBlur(name);
    if (onChange) onChange(date, name);
  }

  /**
   * Callback for when the `Input` component get focused.
   */
  function handleFocus() {
    if (!state.isExpanded) {
      dispatch({
        type: 'expand-picker'
      });
      if (onFocus) onFocus(name);
      setTimeout(() => {
        var _inputRef$current2;
        return (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.select();
      }, 0);
    }
  }
  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const date = (0, _chronoNode.parseDate)(event.target.value);
      if (date) {
        var _inputRef$current3;
        if (onChange) onChange(date, name);
        (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.select();
      }
    } else if (event.key === 'Escape') {
      setDisplayed(value ? (0, _dateFns.format)(value, 'MMMM do yyyy') : value);
      setTimeout(() => {
        var _inputRef$current4;
        return (_inputRef$current4 = inputRef.current) === null || _inputRef$current4 === void 0 ? void 0 : _inputRef$current4.select();
      }, 0);
    }
  }
  return /*#__PURE__*/_react.default.createElement(Container, {
    "aria-disabled": disabled,
    "aria-expanded": state.isExpanded,
    "aria-owns": idRef.current,
    "data-testid": testID,
    ref: containerRef
  }, /*#__PURE__*/_react.default.createElement(Control, null, /*#__PURE__*/_react.default.createElement(Input, {
    name: name,
    onChange: handleChange,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    placeholder: placeholder || 'Select a date...',
    ref: inputRef,
    type: "text",
    value: displayed
  })), /*#__PURE__*/_react.default.createElement(Calendar, {
    "aria-modal": "true",
    "aria-label": "Choose Date",
    id: idRef.current,
    role: "dialog"
  }, /*#__PURE__*/_react.default.createElement(_CalendarPicker.CalendarPicker, {
    maxDate: maxDate,
    minDate: minDate,
    onDatePress: handleDatePress,
    selected: value
  })));
}